import PropTypes from 'prop-types';
import {
  StarIcon,
  UserIcon,
  CheckCircleIcon,
  HandThumbUpIcon,
  HandThumbDownIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

const FeedbackCard = ({
  feedback,
  onMarkHelpful,
  showShareTitle = false,
  className = ''
}) => {
  const {
    reviewerName,
    reviewerRole,
    overallRating,
    quickFeedback,
    generalComments,
    isRead,
    isHelpful,
    createdAt,
    shareTitle
  } = feedback;

  const formatDate = (date) => {
    const d = new Date(date);
    const now = new Date();
    const diffMs = now - d;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return d.toLocaleDateString();
  };

  const getSectionLabel = (id) => {
    const labels = {
      summary: 'Summary',
      experience: 'Experience',
      skills: 'Skills',
      education: 'Education',
      overall_format: 'Overall Format',
      formatting: 'Formatting',
      length: 'Length'
    };
    return labels[id] || id;
  };

  return (
    <div className={`
      bg-white rounded-xl border overflow-hidden
      ${isRead ? 'border-gray-200' : 'border-primary-200 bg-primary-50/30'}
      ${className}
    `}>
      {/* Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
              <UserIcon className="w-5 h-5 text-gray-500" />
            </div>
            <div>
              <p className="font-medium text-gray-900">{reviewerName}</p>
              {reviewerRole && (
                <p className="text-sm text-gray-500">{reviewerRole}</p>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                star <= overallRating ? (
                  <StarIconSolid key={star} className="w-4 h-4 text-yellow-400" />
                ) : (
                  <StarIcon key={star} className="w-4 h-4 text-gray-300" />
                )
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-1">{formatDate(createdAt)}</p>
          </div>
        </div>

        {showShareTitle && shareTitle && (
          <p className="text-xs text-gray-500 mt-2">
            On: <span className="font-medium">{shareTitle}</span>
          </p>
        )}
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Would interview badge */}
        {quickFeedback?.wouldInterview !== null && (
          <div className={`
            inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium
            ${quickFeedback.wouldInterview
              ? 'bg-green-100 text-green-700'
              : 'bg-red-100 text-red-700'
            }
          `}>
            <CheckCircleIcon className="w-4 h-4" />
            {quickFeedback.wouldInterview ? 'Would interview' : 'Would not interview'}
          </div>
        )}

        {/* Section feedback */}
        <div className="flex flex-wrap gap-3">
          {quickFeedback?.bestSection && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-500">Strongest:</span>
              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                {getSectionLabel(quickFeedback.bestSection)}
              </span>
            </div>
          )}
          {quickFeedback?.needsImprovement && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-500">Improve:</span>
              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                {getSectionLabel(quickFeedback.needsImprovement)}
              </span>
            </div>
          )}
        </div>

        {/* Comments */}
        {generalComments && (
          <p className="text-sm text-gray-700 leading-relaxed">
            &quot;{generalComments}&quot;
          </p>
        )}
      </div>

      {/* Footer - Was this helpful? */}
      {onMarkHelpful && (
        <div className="px-4 py-3 border-t border-gray-100 bg-gray-50">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Was this helpful?</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onMarkHelpful(feedback.id, true)}
                className={`
                  p-1.5 rounded transition-colors
                  ${isHelpful === true
                    ? 'bg-green-100 text-green-600'
                    : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                  }
                `}
              >
                <HandThumbUpIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => onMarkHelpful(feedback.id, false)}
                className={`
                  p-1.5 rounded transition-colors
                  ${isHelpful === false
                    ? 'bg-red-100 text-red-600'
                    : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                  }
                `}
              >
                <HandThumbDownIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

FeedbackCard.propTypes = {
  feedback: PropTypes.shape({
    id: PropTypes.string.isRequired,
    reviewerName: PropTypes.string,
    reviewerRole: PropTypes.string,
    overallRating: PropTypes.number.isRequired,
    quickFeedback: PropTypes.shape({
      bestSection: PropTypes.string,
      needsImprovement: PropTypes.string,
      wouldInterview: PropTypes.bool
    }),
    generalComments: PropTypes.string,
    isRead: PropTypes.bool,
    isHelpful: PropTypes.bool,
    createdAt: PropTypes.string.isRequired,
    shareTitle: PropTypes.string
  }).isRequired,
  onMarkHelpful: PropTypes.func,
  showShareTitle: PropTypes.bool,
  className: PropTypes.string
};

export default FeedbackCard;
